import { DateTime } from 'luxon';
import { DateRangeTab } from '../../../../../../../components/general/DateRangeInput/constants.ts';
import { determineRangeInfo, parseDate } from '../../../../../../../components/general/DateRangeInput/utils/dateRangeUtils.ts';

const ISO_DATE = 'yyyy-MM-dd';
const DISPLAY_DATE = 'dd-MM-yyyy';

export interface VatReturnQuarterOption {
    dateFrom: string;
    dateTo: string;
    label: string;
    months: string;
    isInProgress: boolean;
}

function dutchDate(isoDate: string): DateTime {
    return DateTime.fromISO(isoDate).setLocale('nl');
}

function quarterLabel(quarterStart: DateTime): string {
    return `Q${quarterStart.quarter} ${quarterStart.year}`;
}

function quarterMonths(quarterStart: DateTime): string {
    return `${quarterStart.toFormat('LLL')} – ${quarterStart.plus({ months: 2 }).toFormat('LLL')}`;
}

export function vatReturnPeriodKind(periodStart: string, periodEnd: string): DateRangeTab {
    return determineRangeInfo({ from: parseDate(periodStart), to: parseDate(periodEnd) }).tab;
}

export function isSavableVatReturnPeriod(periodStart: string, periodEnd: string): boolean {
    const kind = vatReturnPeriodKind(periodStart, periodEnd);
    return kind === DateRangeTab.Month || kind === DateRangeTab.Quarter;
}

const PERIOD_LABELS: Record<DateRangeTab, (periodStart: string, periodEnd: string) => string> = {
    [DateRangeTab.Quarter]: (periodStart) => quarterLabel(dutchDate(periodStart)),
    [DateRangeTab.Month]: (periodStart) => dutchDate(periodStart).toFormat('LLLL yyyy'),
    [DateRangeTab.Year]: (periodStart) => String(dutchDate(periodStart).year),
    [DateRangeTab.Custom]: (periodStart, periodEnd) => vatReturnPeriodRangeText(periodStart, periodEnd),
};

export function vatReturnPeriodLabel(periodStart: string, periodEnd: string): string {
    return PERIOD_LABELS[vatReturnPeriodKind(periodStart, periodEnd)](periodStart, periodEnd);
}

export function vatReturnPeriodRangeText(periodStart: string, periodEnd: string, separator = ' – '): string {
    return `${dutchDate(periodStart).toFormat(DISPLAY_DATE)}${separator}${dutchDate(periodEnd).toFormat(DISPLAY_DATE)}`;
}

export function vatReturnPeriodMonths(periodStart: string, periodEnd: string): string | null {
    if (vatReturnPeriodKind(periodStart, periodEnd) !== DateRangeTab.Quarter) {
        return null;
    }
    return quarterMonths(dutchDate(periodStart));
}

export function vatReturnToday(moment: Date): string {
    return DateTime.fromJSDate(moment).toFormat(ISO_DATE);
}

export function vatReturnPeriodYear(periodStart: string): number {
    return dutchDate(periodStart).year;
}

export function vatReturnVersionCount(count: number): string {
    return count === 1 ? '1 versie' : `${count} versies`;
}

export function recentVatReturnQuarters(today: Date, count: number): VatReturnQuarterOption[] {
    const currentQuarter = DateTime.fromJSDate(today).setLocale('nl').startOf('quarter');
    return Array.from({ length: count }, (_, offset) => {
        const start = currentQuarter.minus({ quarters: offset });
        return {
            dateFrom: start.toFormat(ISO_DATE),
            dateTo: start.endOf('quarter').toFormat(ISO_DATE),
            label: quarterLabel(start),
            months: quarterMonths(start),
            isInProgress: offset === 0,
        };
    });
}
